import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "./apiConfig";
import { FaEnvelope, FaKey, FaSpinner } from "react-icons/fa";
import Layout from "./HeaderLayouts";
import fireworksBg from "../assets/fireworks.webp";

const ForgotPassword = () => {
const [step, setStep] = useState(1); // 1: Email, 2: Verification Code
const [email, setEmail] = useState("");
const [verificationCode, setVerificationCode] = useState("");
const [errorMessage, setErrorMessage] = useState("");
const [successMessage, setSuccessMessage] = useState("");
const [isLoading, setIsLoading] = useState(false);
const navigate = useNavigate();

// Add spinner animation
React.useEffect(() => {
const style = document.createElement('style');
style.innerHTML = `
@keyframes spin {
0% { transform: rotate(0deg); }
100% { transform: rotate(360deg); }
}`;
document.head.appendChild(style);
return () => document.head.removeChild(style);
}, []);

// Step 1: Send Verification Code
const handleSendCode = async () => {
if (!email.trim()) {
setErrorMessage("Please enter your email");
return;
}

setIsLoading(true);
setErrorMessage("");
setSuccessMessage("");

try {
const response = await axios.post(
`${API_BASE_URL}api/Crackers/SendVerificationCode`,
{ 
email: email.trim(),
companyId: 3
},
{ headers: { "Content-Type": "application/json" } }
);

if (response.data.statusCode === 200) {
setSuccessMessage("Verification code sent to your email!");
setStep(2);
} else {
setErrorMessage(response.data.statusDesc || "Failed to send verification code");
}
} catch (error) {
setErrorMessage(error.response?.data?.statusDesc || "Unable to send verification code. Please try again.");
} finally {
setIsLoading(false);
}
};

// Step 2: Verify Code
const handleVerifyCode = async () => {
if (!verificationCode.trim()) {
setErrorMessage("Please enter the verification code");
return;
}

// Convert to INT for API
const codeInt = parseInt(verificationCode.trim(), 10);
if (isNaN(codeInt)) {
setErrorMessage("Please enter a valid numeric code");
return;
}

setIsLoading(true);
setErrorMessage("");
setSuccessMessage("Verifying code...");

try {
const response = await axios.post(
`${API_BASE_URL}api/Crackers/VerifyCode`,
{ 
email: email.trim(),
code: codeInt,
companyId: 3
},
{ headers: { "Content-Type": "application/json" } }
);

if (response.data.statusCode === 200) {
setSuccessMessage("Code verified successfully! Redirecting...");
// Navigate to reset password screen with email and code (as INT)
setTimeout(() => {
navigate("/ResetPassword", { state: { email: email.trim(), verificationCode: codeInt} });
}, 1000);
} else {
setSuccessMessage("");
setErrorMessage(response.data.statusDesc || "Invalid verification code");
}
} catch (error) {
setSuccessMessage("");
setErrorMessage(error.response?.data?.statusDesc || "Invalid verification code. Please try again.");
} finally {
setIsLoading(false);
}
};

return (
<>
<Layout />
<div style={styles.container}>
<div style={styles.overlay}></div>
<div style={styles.formContainer}>
<h1 style={styles.header}>
{step === 1 && "Forgot Password"}
{step === 2 && "Verify Code"}
</h1>

{/* Step 1: Email Input */}
{step === 1 && (
<>
<p style={styles.description}>
Enter your email address and we'll send you a verification code
</p>
<div style={styles.inputGroup}>
<FaEnvelope style={styles.icon} />
<input
type="email"
placeholder="Enter your Email"
value={email}
onChange={(e) => setEmail(e.target.value)}
style={styles.input}
onKeyPress={(e) => e.key === 'Enter' && handleSendCode()}
/>
</div>
<button
onClick={handleSendCode}
disabled={isLoading}
style={{
...styles.actionButton,
opacity: isLoading ? 0.8 : 1,
cursor: isLoading ? "not-allowed" : "pointer",
}}
>
{isLoading ? (
<>
<FaSpinner style={styles.spinner} /> Sending...
</>
) : (
"Send Verification Code"
)}
</button>
</>
)}

{/* Step 2: Verification Code Input */}
{step === 2 && (
<>
<p style={styles.description}>
Enter the verification code sent to {email}
</p>
<div style={styles.inputGroup}>
<FaKey style={styles.icon} />
<input
type="text"
placeholder="Enter Verification Code"
value={verificationCode}
onChange={(e) => setVerificationCode(e.target.value)}
style={styles.input}
maxLength={6}
onKeyPress={(e) => e.key === 'Enter' && handleVerifyCode()}
/>
</div>
<button
onClick={handleVerifyCode}
disabled={isLoading}
style={{
...styles.actionButton,
opacity: isLoading ? 0.8 : 1,
cursor: isLoading ? "not-allowed" : "pointer",
}}
>
{isLoading ? (
<>
<FaSpinner style={styles.spinner} /> Verifying Code...
</>
) : (
"Verify Code"
)}
</button>
<button
onClick={() => setStep(1)}
style={styles.backButton}
>
Back to Email
</button>
</>
)}

{/* Success Message */}
{successMessage && (
<p style={styles.successMessage}>{successMessage}</p>
)}

{/* Error Message */}
{errorMessage && (
<p style={styles.errorMessage}>{errorMessage}</p>
)}

{/* Back to Login */}
<div style={styles.backToLogin}>
<span
onClick={() => navigate("/AdminLogin")}
style={styles.loginLink}
>
Back to Login
</span>
</div>
</div>
</div>
</>
);
};

const styles = {
container: {
minHeight: "calc(100vh - 120px)",
display: "flex",
alignItems: "center",
justifyContent: "center",
padding: "20px",
backgroundImage: `url(${fireworksBg})`,
backgroundSize: "cover",
backgroundPosition: "center",
backgroundRepeat: "no-repeat",
position: "relative",
},
overlay: {
position: "absolute",
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: "rgba(0, 0, 0, 0.5)",
zIndex: 0,
},
formContainer: {
width: "100%",
maxWidth: "400px",
backgroundColor: "rgba(255, 255, 255, 0.95)",
backdropFilter: "blur(10px)",
WebkitBackdropFilter: "blur(10px)",
padding: "40px 30px",
borderRadius: "12px",
boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
textAlign: "center",
border: "1px solid rgba(83, 42, 81, 0.3)",
position: "relative",
zIndex: 1,
},
header: {
fontSize: "28px",
fontWeight: "600",
color: "#532a51ff",
marginBottom: "15px",
fontFamily: "'Poppins', sans-serif",
},
description: {
fontSize: "14px",
color: "#666",
marginBottom: "25px",
lineHeight: "1.5",
},
inputGroup: {
display: "flex",
alignItems: "center",
marginBottom: "20px",
background: "rgba(255, 255, 255, 0.9)",
borderRadius: "8px",
padding: "12px 15px",
border: "1px solid rgba(83, 42, 81, 0.2)",
boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
},
icon: {
color: "#532a51ff",
fontSize: "18px",
marginRight: "12px",
},
input: {
flex: 1,
border: "none",
outline: "none",
fontSize: "14px",
background: "transparent",
fontFamily: "'Poppins', sans-serif",
color: "#333",
},
actionButton: {
width: "100%",
borderRadius: "8px",
color: "#fff",
padding: "14px",
fontSize: "15px",
fontWeight: "600",
border: "none",
background: "linear-gradient(to right, #532a51ff, #532a51ff)",
boxShadow: "0 4px 12px rgba(83, 42, 81, 0.3)",
marginBottom: "10px",
cursor: "pointer",
transition: "all 0.3s ease",
display: "flex",
alignItems: "center",
justifyContent: "center",
gap: "8px",
},
spinner: {
animation: "spin 1s linear infinite",
},
backButton: {
width: "100%",
borderRadius: "8px",
color: "#532a51ff",
padding: "12px",
fontSize: "14px",
fontWeight: "600",
border: "2px solid #532a51ff",
background: "transparent",
marginTop: "10px",
cursor: "pointer",
transition: "all 0.3s ease",
},
successMessage: {
color: "#2e7d32",
fontSize: "13px",
margin: "15px 0",
padding: "12px",
backgroundColor: "rgba(76, 175, 80, 0.1)",
borderRadius: "6px",
border: "1px solid rgba(76, 175, 80, 0.3)",
fontWeight: "500",
},
errorMessage: {
color: "#c62828",
fontSize: "13px",
margin: "15px 0",
padding: "12px",
backgroundColor: "rgba(244, 67, 54, 0.1)",
borderRadius: "6px",
border: "1px solid rgba(244, 67, 54, 0.3)",
fontWeight: "500",
},
backToLogin: {
marginTop: "25px",
fontSize: "14px",
},
loginLink: {
color: "#532a51ff",
fontWeight: "600",
cursor: "pointer",
textDecoration: "underline",
},
};

export default ForgotPassword;
